import { Alert } from '@mui/material';
import {
  CreateInteractionBody,
  InteractionType,
  UpdateInteractionBody,
} from '@loyalty/shared-common/profile-api-client';
import { ReactNode, createContext, useCallback, useContext, useState } from 'react';
import { useAlerts } from '@qualifio/ui-commons';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { uuid } from '@loyalty/shared-common';
import { InteractionFormValues } from './components/detail/InteractionForm';
import { IsLoading, useLoaders } from '../shared/context/context-utils';
import { getStarterDetailTemplate } from './starter-detail-templates';
import { useAccounts } from '../accounts/AccountsContext';
import { useApi } from '../shared/api-client/ApiClientProvider';

export interface InteractionsContextValue {
  interactions: InteractionFormValues[];
  selectedInteraction: InteractionFormValues | undefined;
  createInteraction: () => void;
  addInteraction: (interaction: CreateInteractionBody) => Promise<void>;
  updateInteraction: (interactionId: string, interaction: UpdateInteractionBody) => Promise<void>;
  deleteInteraction: (interactionId: string) => Promise<void>;
  getInteractions: () => void;
  setSelectedInteraction: (interaction: InteractionFormValues | undefined) => void;
  isLoading: IsLoading;
}

export const InteractionsContext = createContext<InteractionsContextValue>(
  {} as InteractionsContextValue,
);

export function useInteractionsContext() {
  return useContext(InteractionsContext);
}

export function InteractionsProvider({ children }: { children: ReactNode }) {
  const { currentAccount } = useAccounts();
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const { profileApi } = useApi();
  const queryClient = useQueryClient();
  const [interactions, setInteractions] = useState<InteractionFormValues[]>([]);
  const [error, setError] = useState<unknown>();
  const [selectedInteraction, setSelectedInteraction] = useState<InteractionFormValues>();

  const { addLoader, removeLoader, isLoading } = useLoaders();

  const invalidateInteractionQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['interactions', currentAccount.id] });
  }, [currentAccount.id, queryClient]);

  const createInteraction = useCallback(() => {
    const type: InteractionType = 'STORE_RECEIPT';
    const interaction: InteractionFormValues = {
      id: uuid(),
      accountId: currentAccount.id,
      name: '',
      type,
      detailTemplate: getStarterDetailTemplate(type),
      contentExample: null,
      createdAt: '',
      updatedAt: '',
      isNew: true,
    };

    setInteractions(prev => [interaction, ...prev]);
    setSelectedInteraction(interaction);
  }, [currentAccount.id]);

  const addInteraction = useCallback(
    async (interaction: CreateInteractionBody) => {
      addLoader('addInteraction');

      return profileApi
        .createInteraction(currentAccount.id, interaction)
        .then(newInteraction => {
          setInteractions(prev =>
            prev.map(item => (item.id === selectedInteraction?.id ? newInteraction : item)),
          );
          setSelectedInteraction(newInteraction);
          invalidateInteractionQueries();
          notify(t('shared.saved'), 'success');
        })
        .catch(setError)
        .finally(() => removeLoader('addInteraction'));
    },
    [
      addLoader,
      currentAccount.id,
      invalidateInteractionQueries,
      notify,
      profileApi,
      removeLoader,
      selectedInteraction?.id,
      t,
    ],
  );

  const updateInteraction = useCallback(
    async (interactionId: string, interaction: UpdateInteractionBody) => {
      addLoader('updateInteraction');

      return profileApi
        .updateInteraction(interactionId, interaction)
        .then(updatedInteraction => {
          setInteractions(prev =>
            prev.map(item => (item.id === updatedInteraction.id ? updatedInteraction : item)),
          );
          setSelectedInteraction(updatedInteraction);
          invalidateInteractionQueries();
          notify(t('shared.saved'), 'success');
        })
        .catch(setError)
        .finally(() => removeLoader('updateInteraction'));
    },
    [addLoader, invalidateInteractionQueries, notify, profileApi, removeLoader, t],
  );

  const deleteInteraction = useCallback(
    async (interactionId: string) => {
      addLoader('deleteInteraction');

      const interaction = interactions.find(({ id }) => id === interactionId);

      if (interaction?.isNew) {
        setInteractions(prev => prev.filter(item => item.id !== interactionId));
        if (selectedInteraction?.id === interactionId) {
          setSelectedInteraction(undefined);
        }
        removeLoader('deleteInteraction');
        return;
      }

      await profileApi
        .deleteInteraction(interactionId)
        .then(() => {
          setInteractions(prev => prev.filter(item => item.id !== interactionId));
          setSelectedInteraction(prev => (prev?.id === interactionId ? undefined : prev));
          invalidateInteractionQueries();
          notify(t('shared.saved'), 'success');
        })
        .catch(err => {
          notify(t('interactions.errors.delete', { error: String(err) }), 'error');
        })
        .finally(() => removeLoader('deleteInteraction'));
    },
    [
      addLoader,
      interactions,
      invalidateInteractionQueries,
      notify,
      profileApi,
      removeLoader,
      selectedInteraction?.id,
      t,
    ],
  );

  const getInteractions = useCallback(() => {
    addLoader('getInteractions');

    profileApi
      .listInteractions(currentAccount.id)
      .then(setInteractions)
      .catch(setError)
      .finally(() => removeLoader('getInteractions'));
  }, [addLoader, currentAccount.id, profileApi, removeLoader]);

  const context: InteractionsContextValue = {
    interactions,
    selectedInteraction,
    createInteraction,
    addInteraction,
    updateInteraction,
    deleteInteraction,
    getInteractions,
    setSelectedInteraction,
    isLoading,
  };

  return error ? (
    <Alert severity="error">{String(error)}</Alert>
  ) : (
    <InteractionsContext.Provider value={context}>{children}</InteractionsContext.Provider>
  );
}
